import React, { useEffect, useState } from "react";
import { Box, Modal, Skeleton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TopTitleBanner from "../component/TopTitleBanner";
import "../css/dashboard.css";
import "../css/setting.css";
import { useAuth } from "../contexts/AuthContext";
import "react-toastify/dist/ReactToastify.css";
import "react-tippy/dist/tippy.css";
import { toast } from "react-toastify";
import { roles } from "../firebase.collection";
import {
  deleteDoc,
  doc,
  documentId,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
  collection,
} from "firebase/firestore";
import { db } from "../firebase";
import TitleMedium5 from "../component/Titles/TitleMedium5";
import FormLineMedium5 from "../component/FormLineMedium5";
import TopTitleFormComponent from "../component/forms/TopTitleFormComponent";
import RolesPermissionsOverview from "../component/RolesPermissionsOverview";

export default function RolesContent() {
  const [allRoles, setAllRoles] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [filterSearch, setFilterSearch] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [whitelistEmail, setWhitelistEmail] = useState("");
  const [whitelistEntries, setWhitelistEntries] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [isWhitelistLoading, setIsWhitelistLoading] = useState(true);
  const [isWhitelistSaving, setIsWhitelistSaving] = useState(false);
  const [openWhitelistModal, setOpenWhitelistModal] = useState(false);
  const [isWhitelistExpanded, setIsWhitelistExpanded] = useState(false);

  const { currentUser } = useAuth();

  const userIdState = currentUser ? currentUser.uid : "";
  const styleModalGeneral = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 520,
    height: 400,
    borderRadius: 0,
    backgroundColor: "white",
    boxShadow: 24,
  };

  // useEffect(() => {
  //   if (currentUser?.email) {
  //     recordMetrics("Roles", "pageLoad", currentUser.email, "read", "bodhi");
  //   }
  // }, [currentUser.email]);

  useEffect(() => {
    const roleCollection = collection(db, "roles");
    const unsubscribe = onSnapshot(
      roleCollection,
      { includeMetadataChanges: true },
      (querySnapshot) => {
        setAllRoles(
          querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
        setIsRolesLoading(querySnapshot.metadata.fromCache);
      },
      (error) => {
        console.error(error);
        setIsRolesLoading(false);
      }
    );
    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    const listQuery = query(
      collection(db, "externalAccessWhitelist"),
      orderBy("emailLower")
    );
    const unsubscribe = onSnapshot(
      listQuery,
      (snapshot) => {
        const items = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        setWhitelistEntries(items);
        setIsWhitelistLoading(false);
      },
      (error) => {
        console.error(error);
        setIsWhitelistLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (currentUser) {
      queryRole();
    } else {
      console.log("No user id");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  function queryRole() {
    onSnapshot(
      query(roles, where(documentId(), "==", userIdState)),
      (querySnapshot) => {
        const doc = querySnapshot.docs[0];
        if (doc && doc.exists) {
          const role = doc.data().role;

          setUserRole(role);
        } else {
          console.log("No role for this user");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }
    );
  }

  const isSuperAdmin =
    userRole === "Super Admin" || userRole === "Clinical Director";

  const filteredRoles = allRoles.filter((role) => {
    const searchValue = filterSearch.trim().toLowerCase();
    const searchableUserFields = [
      role.userEmail,
      role.displayName,
      role.phoneNumber,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      (searchValue === "" || searchableUserFields.includes(searchValue)) &&
      (filterRole === "" || role.role === filterRole)
    );
  });

  const handleAddWhitelist = async () => {
    const email = whitelistEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Email is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    setIsWhitelistSaving(true);
    try {
      await setDoc(
        doc(db, "externalAccessWhitelist", email),
        {
          email,
          emailLower: email,
          enabled: true,
          updatedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
      setWhitelistEmail("");
      setOpenWhitelistModal(false);
      toast.success("Whitelist updated.");
    } catch (error) {
      console.error(error);
      toast.error("Unable to update whitelist.");
    }
    setIsWhitelistSaving(false);
  };

  const handleCloseWhitelistModal = () => {
    setOpenWhitelistModal(false);
    setWhitelistEmail("");
  };

  const handleRemoveWhitelist = async (emailId) => {
    if (!window.confirm("Remove this email from the whitelist?")) return;
    try {
      await deleteDoc(doc(db, "externalAccessWhitelist", emailId));
      toast.success("Email removed.");
    } catch (error) {
      console.error(error);
      toast.error("Unable to remove email.");
    }
  };

  return (
    <div className="right-container-settings">
      {isSuperAdmin ? (
        <div className="roles-management-shell">
          <Modal
            open={openWhitelistModal}
            onClose={handleCloseWhitelistModal}
            aria-labelledby="whitelist-modal-title"
            aria-describedby="whitelist-modal-description"
            slotProps={{
              backdrop: {
                style: { backgroundColor: "transparent" },
              },
            }}
          >
            <Box sx={styleModalGeneral}>
              <div
                style={{
                  width: "auto",
                  height: "100%",
                }}
              >
                <div className="line1-modal-percent">
                  <TopTitleFormComponent
                    title="Whitelisted email"
                    closemodal={handleCloseWhitelistModal}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    marginTop: "3px",
                    height: "80%",
                    overflowY: "auto",
                    paddingLeft: "20px",
                    paddingRight: "20px",
                    paddingBottom: "10px",
                  }}
                >
                  <input
                    type="text"
                    placeholder="Email"
                    value={whitelistEmail}
                    onChange={(e) => setWhitelistEmail(e.target.value)}
                    className="filter-input"
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="line4-modal-percent-bis">
                  <>
                    <button
                      type="button"
                      onClick={handleCloseWhitelistModal}
                      style={{
                        border: "1px solid #cfcfcf",
                        background: "white",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleAddWhitelist}
                      disabled={isWhitelistSaving}
                      style={{
                        border: "none",
                        background: "#1565C0",
                        color: "white",
                        padding: "8px 16px",
                        borderRadius: "6px",
                        cursor: "pointer",
                        marginLeft: "15px",
                      }}
                    >
                      Add
                    </button>
                  </>
                </div>
              </div>
            </Box>
          </Modal>
          <TopTitleBanner title={"Role management"} />

          <div className="roles-permissions-entry">
            <RolesPermissionsOverview />
          </div>

          <div className="roles-page-intro">
            <div className="roles-onboarding-panel">
              <div className="roles-onboarding-title">To add a user</div>
              <div className="roles-onboarding-steps">
                <div className="roles-onboarding-step">
                  <span className="roles-onboarding-index">1</span>
                  <div>
                    <div className="roles-onboarding-step-title">
                      Create G Suite email
                    </div>
                    <div className="roles-onboarding-step-text">
                      Create the staff email in Google Workspace.
                    </div>
                  </div>
                </div>
                <div className="roles-onboarding-step">
                  <span className="roles-onboarding-index">2</span>
                  <div>
                    <div className="roles-onboarding-step-title">
                      First Bodhi login
                    </div>
                    <div className="roles-onboarding-step-text">
                      The user logs in once and starts as Normal User.
                    </div>
                  </div>
                </div>
                <div className="roles-onboarding-step">
                  <span className="roles-onboarding-index">3</span>
                  <div>
                    <div className="roles-onboarding-step-title">
                      Assign role here
                    </div>
                    <div className="roles-onboarding-step-text">
                      Update their role from the user list below.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <section className="roles-toolbar-panel">
            <div className="roles-toolbar-title-row">
              <div>
                <div className="roles-section-title">User roles</div>
                <div className="roles-section-subtitle">
                  {isRolesLoading
                    ? "Loading users..."
                    : `${filteredRoles.length} of ${allRoles.length} users shown`}
                </div>
              </div>
            </div>
            <div className="roles-filter-grid">
              <input
                type="text"
                placeholder="Search by email, name, or phone"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                className="roles-filter-control"
              />
              <select
                value={filterRole}
                onChange={(e) => setFilterRole(e.target.value)}
                className="roles-filter-control"
              >
                <option value="">All roles</option>
                {Array.from(new Set(allRoles.map((role) => role.role)))
                  .sort()
                  .map((roleName) => (
                    <option key={roleName} value={roleName}>
                      {roleName}
                    </option>
                  ))}
              </select>
            </div>
          </section>

          <section className="roles-users-panel">
            <TitleMedium5 />
            {isRolesLoading ? (
              <div className="roles-skeleton-list">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div className="roles-table-row roles-skeleton-row" key={index}>
                    <div className="roles-col roles-col-email">
                      <Skeleton variant="text" width="78%" height={20} />
                    </div>
                    <div className="roles-col roles-col-name">
                      <Skeleton variant="text" width="62%" height={20} />
                    </div>
                    <div className="roles-col roles-col-role">
                      <Skeleton variant="rounded" width={92} height={24} />
                    </div>
                    <div className="roles-col roles-col-phone">
                      <Skeleton variant="text" width="52%" height={20} />
                    </div>
                    <div className="roles-col roles-col-actions">
                      <div className="roles-row-actions">
                        <Skeleton variant="rounded" width={32} height={32} />
                        <Skeleton variant="rounded" width={32} height={32} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredRoles.length === 0 ? (
              <div className="roles-empty-state">No users match these filters.</div>
            ) : (
              filteredRoles.map((role) => (
                <FormLineMedium5
                  key={role.id}
                  userId={role.id}
                  userName={role.displayName}
                  userRole={role.role}
                  userPhone={role.phoneNumber}
                  userEmail={role.userEmail}
                />
              ))
            )}
          </section>

          <section className="roles-section-panel">
            <div className="roles-section-header">
              <button
                type="button"
                className="roles-section-heading-button"
                onClick={() => setIsWhitelistExpanded(!isWhitelistExpanded)}
              >
                <span className="roles-section-title">Whitelisted emails</span>
                <span className="roles-section-subtitle">
                  {isWhitelistLoading
                    ? "Loading external access list"
                    : `${whitelistEntries.length} external account${
                        whitelistEntries.length === 1 ? "" : "s"
                      } allowed`}
                </span>
              </button>
              <div className="roles-section-actions">
                <button
                  type="button"
                  onClick={() => setOpenWhitelistModal(true)}
                  className="roles-primary-soft-button"
                >
                  <AddIcon fontSize="small" />
                  <span>Add email</span>
                </button>
                <button
                  type="button"
                  className="roles-icon-toggle"
                  onClick={() => setIsWhitelistExpanded(!isWhitelistExpanded)}
                  aria-label={
                    isWhitelistExpanded
                      ? "Collapse whitelist"
                      : "Expand whitelist"
                  }
                >
                  {isWhitelistExpanded ? (
                    <ExpandLessIcon fontSize="small" />
                  ) : (
                    <ExpandMoreIcon fontSize="small" />
                  )}
                </button>
              </div>
            </div>
            {isWhitelistExpanded && (
              <div className="roles-whitelist-body">
                {isWhitelistLoading ? (
                  <div className="roles-empty-state">Loading whitelist...</div>
                ) : whitelistEntries.length === 0 ? (
                  <div className="roles-empty-state">No emails added.</div>
                ) : (
                  <div className="roles-whitelist-list">
                    {whitelistEntries.map((entry) => (
                      <div key={entry.id} className="roles-whitelist-row">
                        <div className="roles-whitelist-email">
                          {entry.email}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveWhitelist(entry.id)}
                          className="roles-link-danger"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
        </div>
      ) : (
        <div>
          <TopTitleBanner title={"Role management"} />
          <p style={{ marginLeft: "60px", color: "red", fontWeight: "bold " }}>
            --You don't have Super-admin credentials to access this page--
          </p>
        </div>
      )}
    </div>
  );
}
